// Shared primitives, hooks, and date helpers used across all report
// sections. Keeping them in one file avoids 12 tiny imports per section and
// gives a single source of truth for the `useReport` data-fetching hook.
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { format } from "date-fns";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";
import { Download } from "@phosphor-icons/react";
import { downloadCsv } from "@/utils/downloadCsv";

export const COLORS = ["#B85D43", "#728A6D", "#D29856", "#8C8883"];

export const PRESETS = {
  daily: { label: "Daily (today)" },
  weekly: { label: "Weekly (last 7 days)" },
  monthly: { label: "Monthly (last 30 days)" },
  custom: { label: "Custom range" },
};

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

export const fmtDate = (d) => format(d, "yyyy-MM-dd");

export function presetToRange(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (preset === "daily") return { start: today, end: addDays(today, 1) };
  if (preset === "weekly") return { start: addDays(today, -6), end: addDays(today, 1) };
  if (preset === "monthly") return { start: addDays(today, -29), end: addDays(today, 1) };
  return { start: addDays(today, -6), end: addDays(today, 1) };
}

export const buildQuery = (filt, isSingleDay) => {
  const p = new URLSearchParams();
  if (isSingleDay) {
    p.set("date", fmtDate(filt.start));
  } else {
    p.set("start_date", fmtDate(filt.start));
    p.set("end_date", fmtDate(filt.end));
  }
  if (filt.startTime) p.set("start_time", filt.startTime);
  if (filt.endTime) p.set("end_time", filt.endTime);
  if (filt.weekdays && filt.weekdays.length > 0 && filt.weekdays.length < 7) {
    p.set("weekdays", filt.weekdays.slice().sort((a, b) => a - b).join(","));
  }
  return p.toString();
};

// ---------------------------------------------------------------------------
// Shared "Export CSV" pill — every report section drops it in the top-right
// of its card. Uses the same filter object as useReport so the CSV matches
// what's on screen 1:1.
// ---------------------------------------------------------------------------
export const ExportCsvButton = ({ path, filter, isSingleDay, inclusiveEnd = false, label = "Export CSV", testId }) => {
  const onClick = () => {
    let qs = buildQuery(filter, isSingleDay);
    if (inclusiveEnd) {
      // Wages endpoint uses INCLUSIVE end_date — match useReport's back-off
      // so the CSV window matches the on-screen table exactly.
      const inclusiveEndStr = fmtDate(addDays(filter.end, -1));
      const params = new URLSearchParams(qs);
      params.set("end_date", inclusiveEndStr);
      qs = params.toString();
    }
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    downloadCsv(`${API}${path}?${qs}`);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
      data-testid={testId || "export-csv-btn"}
    >
      <Download size={14} /> {label}
    </button>
  );
};

// ----------------------------------------------------------------------------
// useReport — fetches the requested report and, when `filter.comparePrev` is
// set on a range-based report, also fetches the equivalent-length previous
// window in parallel. Returns `[data, loading, prevData]`.
// ----------------------------------------------------------------------------
export const useReport = (path, filter, isSingleDay, opts = {}) => {
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setPrevData(null);

    const buildQs = (filt) => {
      let qs = buildQuery(filt, isSingleDay);
      // Some endpoints expect an *inclusive* end_date — back off by 1 so the
      // total stops on the last selected day, not the day after.
      if (opts.isWagesEndpoint || opts.inclusiveEnd) {
        const inclusiveEnd = format(addDays(filt.end, -1), "yyyy-MM-dd");
        qs = qs.replace(/end_date=[^&]+/, `end_date=${inclusiveEnd}`);
      }
      if (opts.extraParams) {
        for (const [k, v] of Object.entries(opts.extraParams)) {
          if (v !== undefined && v !== null && v !== "") {
            qs += `&${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
          }
        }
      }
      return qs;
    };

    const wantPrev = filter.comparePrev && !isSingleDay && !opts.skipCompare;
    let prevFilter = null;
    if (wantPrev) {
      const ms = filter.end.getTime() - filter.start.getTime();
      const days = Math.max(1, Math.round(ms / 86_400_000));
      prevFilter = {
        ...filter,
        start: addDays(filter.start, -days),
        end: filter.start,
        comparePrev: false,
      };
    }

    const reqs = [axios.get(`${API}${path}?${buildQs(filter)}`)];
    if (prevFilter) reqs.push(axios.get(`${API}${path}?${buildQs(prevFilter)}`));

    Promise.all(reqs)
      .then((responses) => {
        if (ignore) return;
        setData(responses[0].data);
        setPrevData(responses[1] ? responses[1].data : null);
      })
      .catch(() => toast.error("Couldn't load report"))
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, isSingleDay, JSON.stringify(filter), JSON.stringify(opts)]);

  return [data, loading, prevData];
};

// ----------------------------------------------------------------------------
// Tiny presentation primitives
// ----------------------------------------------------------------------------
export const Loader = () => (
  <div className="text-sm text-muted-foreground">Loading…</div>
);

export const Row = ({ left, right, bold, tone, testId }) => (
  <div
    className={`flex justify-between ${bold ? "font-semibold" : ""} ${tone || ""}`}
    data-testid={testId}
  >
    <span className="font-sans">{left}</span>
    <span>{right}</span>
  </div>
);

export const MiniTile = ({
  label,
  value,
  tone = "text-foreground",
  valueClass = "text-xl font-mono font-semibold",
  testId,
  extra,
}) => (
  <div className="rounded-lg border border-border p-3" data-testid={testId}>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`mt-1 ${valueClass} ${tone}`}>{value}</div>
    {extra ? <div className="mt-1">{extra}</div> : null}
  </div>
);

// DeltaBadge — chip showing % delta vs the matching previous-period value.
// `invertGood` flips the colour for metrics where lower is better
// (decline rate, fees, refunds).
export const DeltaBadge = ({ current, previous, invertGood = false }) => {
  if (previous === null || previous === undefined) return null;
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (cur === 0 && prev === 0) {
    return (
      <span className="text-[10px] font-mono text-muted-foreground" title="No data either period">
        — vs prev
      </span>
    );
  }
  let pctStr;
  if (prev === 0) {
    pctStr = "new";
  } else {
    pctStr = `${Math.abs(((cur - prev) / prev) * 100).toFixed(1)}%`;
  }
  const up = cur >= prev;
  const good = invertGood ? !up : up;
  const arrow = up ? "▲" : "▼";
  return (
    <span
      className={`text-[10px] font-mono font-medium ${
        good ? "text-green-700" : "text-red-700"
      }`}
      data-testid="delta-badge"
      title={`Previous period: ${typeof previous === "number" ? prev.toFixed(2) : prev}`}
    >
      {arrow} {pctStr} vs prev
    </span>
  );
};

export const DetailStat = ({ label, value, sub, tone = "", bold = false }) => (
  <div className="space-y-0.5">
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`font-mono ${bold ? "text-base font-semibold" : "text-sm"} ${tone}`}>
      {value}
    </div>
    {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
  </div>
);

export const BreakdownTable = ({ title, rows, testIdPrefix }) => (
  <div className="rounded-lg border border-border overflow-hidden">
    <div className="px-3 py-2 bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
      {title}
    </div>
    <div className="divide-y divide-border">
      {rows.map((row) => (
        <div
          key={row.label}
          className="px-3 py-2 flex items-center justify-between gap-3 text-sm"
          data-testid={`${testIdPrefix}-${row.label.replace(/\s+/g, "-")}`}
        >
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">{row.label}</div>
            <div className="text-xs text-muted-foreground">
              {row.count} {row.count === 1 ? "redemption" : "redemptions"} · {row.share_pct}% of total
            </div>
          </div>
          <div className="font-mono font-semibold shrink-0">${row.total_amount.toFixed(2)}</div>
        </div>
      ))}
    </div>
  </div>
);

export const EmployeeTable = ({ rows, columns, testIdPrefix, emptyText }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground rounded-lg bg-secondary/30">
        {emptyText}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border/50" data-testid={`${testIdPrefix}-${i}`}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-3 py-2 ${c.align === "right" ? "text-right font-mono" : ""} ${
                    c.bold ? "font-semibold" : ""
                  } ${c.accent ? "text-accent" : ""}`}
                >
                  {c.fmt(r[c.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const EmployeeBlock = ({ emp, isOpen, onToggle, showToggle }) => (
  <div className="rounded-lg border border-border overflow-hidden" data-testid={`employee-block-${emp.user_id}`}>
    <button
      type="button"
      onClick={showToggle ? onToggle : undefined}
      className={`w-full px-3 py-2 flex items-center justify-between gap-3 bg-secondary/40 ${showToggle ? "cursor-pointer hover:bg-secondary/60" : "cursor-default"} text-left`}
      data-testid={`employee-header-${emp.user_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{emp.name}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-card border border-border px-1.5 py-0.5 rounded">
            {emp.role}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            ${emp.hourly_rate.toFixed(2)}/hr
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0 text-sm">
        <span className="font-mono">{emp.hours_worked.toFixed(2)}h</span>
        <span className="font-mono font-semibold text-accent">
          ${emp.total_pay.toFixed(2)}
        </span>
        {showToggle && (
          <span className="text-muted-foreground text-xs">{isOpen ? "▲" : "▼"}</span>
        )}
      </div>
    </button>

    {isOpen && (
      <div className="px-3 py-3 space-y-3 bg-card">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <DetailStat label="Hours" value={emp.hours_worked.toFixed(2)} />
          <DetailStat label="Shifts" value={emp.shifts} sub={`avg ${emp.avg_shift_hours.toFixed(2)}h`} />
          <DetailStat label="Regular" value={emp.regular_hours.toFixed(2) + "h"} />
          <DetailStat
            label="Overtime"
            value={emp.overtime_hours.toFixed(2) + "h"}
            tone={emp.overtime_hours > 0 ? "text-amber-700" : ""}
          />
          <DetailStat label="Wages" value={`$${emp.wages.toFixed(2)}`} />
          <DetailStat
            label="Tips"
            value={`$${emp.tips.toFixed(2)}`}
            tone={emp.tips > 0 ? "text-accent" : ""}
          />
          <DetailStat label="Total Pay" value={`$${emp.total_pay.toFixed(2)}`} bold />
          <DetailStat
            label="Effective $/hr"
            value={emp.hours_worked > 0 ? `$${(emp.total_pay / emp.hours_worked).toFixed(2)}` : "—"}
            sub="wages + tips"
          />
        </div>
        {emp.orders > 0 && (
          <div className="pt-2 border-t border-border/60">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Sales productivity
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <DetailStat label="Orders" value={emp.orders} />
              <DetailStat label="Sales" value={`$${emp.sales.toFixed(2)}`} />
              <DetailStat label="Avg Ticket" value={`$${emp.avg_ticket.toFixed(2)}`} />
              <DetailStat label="$/hr Sold" value={`$${emp.sales_per_hour.toFixed(2)}`} />
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

export const fillDailySeries = (rows, startStr, endStr) => {
  const byDate = Object.fromEntries(rows.map((r) => [r.date, r]));
  const out = [];
  const start = new Date(startStr + "T00:00:00Z");
  const end = new Date(endStr + "T00:00:00Z");
  for (let d = new Date(start); d < end; d.setUTCDate(d.getUTCDate() + 1)) {
    const iso = d.toISOString().slice(0, 10);
    out.push(byDate[iso] || { date: iso, count: 0, total_amount: 0 });
  }
  return out.map((p) => ({ ...p, label: p.date.slice(5) }));
};

export const PerkSparkline = ({ points }) => (
  <div className="rounded-lg border border-border p-3" data-testid="perk-report-sparkline">
    <div className="flex items-baseline justify-between mb-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        Daily Redemptions ($)
      </div>
      <div className="text-xs text-muted-foreground font-mono">
        {points[0]?.label} → {points[points.length - 1]?.label}
      </div>
    </div>
    <ResponsiveContainer width="100%" height={80}>
      <LineChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <XAxis dataKey="label" hide />
        <YAxis hide domain={[0, "dataMax + 1"]} />
        <Tooltip
          formatter={(v) => [`$${Number(v).toFixed(2)}`, "Given away"]}
          labelFormatter={(l) => `Day ${l}`}
          contentStyle={{ fontSize: 12, padding: "4px 8px" }}
        />
        <Line
          type="monotone"
          dataKey="total_amount"
          stroke="#B85D43"
          strokeWidth={2}
          dot={{ r: 2.5 }}
          activeDot={{ r: 4 }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

// Opens a 80mm thermal-printer-friendly EOD report in a popup and auto-prints.
export const printEODReport = (data) => {
  if (!data) return;
  const win = window.open("", "_blank", "width=720,height=900");
  const split = Object.entries(data.payment_split || {})
    .map(([k, v]) => `<tr><td>${k}</td><td class="right">$${v.toFixed(2)}</td></tr>`)
    .join("");
  const servers = (data.by_server || [])
    .map(
      (s) =>
        `<tr><td>${s.name}</td><td class="right">${s.orders}</td><td class="right">$${s.sales.toFixed(2)}</td><td class="right">$${s.tips.toFixed(2)}</td></tr>`
    )
    .join("");
  const cats = (data.top_categories || [])
    .map((c) => `<tr><td>${c.name}</td><td class="right">$${c.amount.toFixed(2)}</td></tr>`)
    .join("");
  const hourly = (data.hourly_sales || [])
    .filter((h) => h.sales > 0)
    .map(
      (h) =>
        `<tr><td>${String(h.hour).padStart(2, "0")}:00</td><td class="right">$${h.sales.toFixed(2)}</td></tr>`
    )
    .join("");
  win.document.write(`<!doctype html><html><head><title>EOD ${data.date}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap');
      @page { size: 80mm auto; margin: 0; }
      html,body { margin:0; padding:0; }
      body { font-family:'Space Mono',monospace; font-size:11px; width:80mm; padding:4mm 3mm; box-sizing:border-box; color:#000; }
      h1 { font-size:14px; text-align:center; margin:0 0 4px; }
      h2 { font-size:12px; margin:10px 0 4px; border-top:1px dashed #999; padding-top:6px; text-transform:uppercase; letter-spacing:.5px; }
      table { width:100%; border-collapse:collapse; }
      td { padding:2px 0; vertical-align:top; }
      .right { text-align:right; }
      .bold { font-weight:bold; }
      .small { font-size:9px; color:#444; }
      .center { text-align:center; }
      @media print { body { padding:3mm; } }
    </style></head><body>
    <h1>END OF DAY</h1>
    <div class="center small">${data.date}</div>
    <div class="center small">Printed ${new Date().toLocaleString()}</div>

    <h2>Summary</h2>
    <table>
      <tr><td>Net Sales</td><td class="right bold">$${data.total_sales.toFixed(2)}</td></tr>
      <tr><td>Tax Collected</td><td class="right">$${data.total_tax.toFixed(2)}</td></tr>
      <tr><td>Tips</td><td class="right">$${data.total_tips.toFixed(2)}</td></tr>
      <tr><td>Tickets</td><td class="right">${data.order_count}</td></tr>
      <tr><td>Avg Ticket</td><td class="right">$${data.avg_ticket.toFixed(2)}</td></tr>
      <tr><td>Refunds (${data.refunded_orders})</td><td class="right">$${data.total_refunded.toFixed(2)}</td></tr>
      <tr><td>Voids</td><td class="right">${data.cancelled_orders}</td></tr>
    </table>

    <h2>Payment Split</h2>
    <table>${split || "<tr><td colspan='2' class='center small'>None</td></tr>"}</table>

    <h2>By Server</h2>
    <table>
      <tr class="bold"><td>Name</td><td class="right">Ord</td><td class="right">Sales</td><td class="right">Tips</td></tr>
      ${servers || "<tr><td colspan='4' class='center small'>—</td></tr>"}
    </table>

    <h2>Top Categories</h2>
    <table>${cats || "<tr><td colspan='2' class='center small'>—</td></tr>"}</table>

    ${hourly ? `<h2>Hourly Sales</h2><table>${hourly}</table>` : ""}

    <h2>Sign-off</h2>
    <div class="small">Manager: ______________________</div>
    <div class="small" style="margin-top:6px">Cash counted: $______________</div>
    <div class="small" style="margin-top:6px">Variance: $______________</div>

    <script>
      window.onload = () => { setTimeout(() => { window.print(); }, 250); };
    </script>
  </body></html>`);
  win.document.close();
};
